import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const METHODS = [['cash', 'Cash from drawer'], ['card', 'Card terminal'], ['bank_transfer', 'Bank transfer'], ['credit', 'Customer account']];

// Full refund → credit note via the existing POS refund (manager PIN, atomic, journals reverse automatically).
export const SaleRefundPanel = ({ refund, onDone }) => {
  const orig = (refund.payment_method || '').toLowerCase();
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(METHODS.some(([m]) => m === orig) ? orig : '');
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  if (!refund.eligible) {
    return (
      <div className="rounded border p-2 text-xs text-muted-foreground" data-testid="sale-refund-unavailable">
        Refund not available: {refund.reason}{refund.credit_note_number ? ` · credit note #${refund.credit_note_number}` : ''}
      </div>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cashier/orders/${refund.order_id}/refund`, { reason, manager_pin: pin, refund_method: method });
      toast.success(`${data.message}. ${data.refund_settlement?.note || ''}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed');
    } finally { setBusy(false); }
  };
  if (!open) {
    return <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="sale-refund-open-btn">Refund / Credit Note (full bill #{refund.order_number})</Button>;
  }
  return (
    <div className="rounded border border-rose-200 bg-rose-50/40 p-3 space-y-2 text-xs" data-testid="sale-refund-form">
      <div className="font-medium">Full refund of bill #{refund.order_number} · SAR {Math.abs(Number(refund.total || 0)).toFixed(2)}</div>
      <div className="text-muted-foreground">Creates a credit note linked to this bill. The original invoice and journal stay unchanged; a reversing journal posts once. Full refund only — partial refunds are not supported.</div>
      <div className="flex flex-wrap gap-1">
        {METHODS.filter(([m]) => m !== 'credit' || orig === 'credit').map(([m, l]) => (
          <Button key={m} size="sm" type="button" variant={method === m ? 'default' : 'outline'} onClick={() => setMethod(m)} data-testid={`sale-refund-method-${m}`}>{l}</Button>
        ))}
      </div>
      <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sale-refund-reason" />
      <Input placeholder="Manager PIN" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="sale-refund-pin" />
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" disabled={busy || !method || reason.trim().length < 3 || !pin} onClick={submit} data-testid="sale-refund-confirm-btn">{busy ? 'Refunding…' : 'Confirm refund'}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="sale-refund-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
};
